/* eslint-disable react/jsx-no-bind */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { remark } from 'remark';
import remarkToReact from 'remark-react';
import { FaRegClipboard as ClipboardIcon } from 'react-icons/fa';

import Config from '../../config';
import Button from '../../shared/button.component';
import Select from '../../shared/reactSelect/select.component';
import Spinner from '../../shared/spinner.component';
import { periodsSelector, companyCodesOptions, userSelector } from '../../shared/store/selectors';
import fetchService from '../../utils/fetchService';
import { handleErrors, parseAsJson } from '../../utils/requestHelpers';
import { isInternalEmailAddress } from '../../userSettings/utils';

type SelectOption = {
  label: string;
  value: string;
};

type AnalysisSuccessResult = {
  status: 'success';
  analysis: string;
  metadata: {
    companyCode: string;
    fromPeriod: string;
    toPeriod: string;
    generatedAt: string;
    finishReason: string;
  };
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
};

type AnalysisNoDataResult = {
  status: 'no_data';
  message: string;
  metadata: {
    companyCode: string;
    fromPeriod: string;
    toPeriod: string;
    generatedAt: string;
  };
};

type AnalysisResult = AnalysisSuccessResult | AnalysisNoDataResult;

const MultiPeriodAnalysisPage: React.FC = () => {
  const periods = useSelector(periodsSelector);
  const companyCodes = useSelector(companyCodesOptions);
  const user = useSelector(userSelector);

  const [selectedCompanyCode, setSelectedCompanyCode] = useState<SelectOption | null>(null);
  const [selectedFromPeriod, setSelectedFromPeriod] = useState<SelectOption | null>(null);
  const [selectedToPeriod, setSelectedToPeriod] = useState<SelectOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<AnalysisSuccessResult | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isLoading) {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isLoading]);

  const isPwcUser = useMemo(() => isInternalEmailAddress(user?.email), [user?.email]);

  const periodOptions = useMemo(
    () => periods.map((p: { name: string; value: string }) => ({ label: p.name, value: p.value })),
    [periods]
  );

  const toPeriodOptions = useMemo(() => {
    if (!selectedFromPeriod) {
      return periodOptions;
    }
    const fromIndex = periodOptions.findIndex((p: SelectOption) => p.value === selectedFromPeriod.value);
    if (fromIndex === -1) {
      return periodOptions;
    }
    return periodOptions.slice(0, fromIndex);
  }, [periodOptions, selectedFromPeriod]);

  const canSubmit = selectedCompanyCode && selectedFromPeriod && selectedToPeriod && !isLoading;

  const handleFromPeriodChange = useCallback((option: SelectOption) => {
    setSelectedFromPeriod(option);
    if (selectedToPeriod && option) {
      const fromIdx = periodOptions.findIndex((p: SelectOption) => p.value === option.value);
      const toIdx = periodOptions.findIndex((p: SelectOption) => p.value === selectedToPeriod.value);
      if (toIdx >= fromIdx) {
        setSelectedToPeriod(null);
      }
    }
  }, [periodOptions, selectedToPeriod]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) {
      return;
    }

    setIsLoading(true);
    setResult(null);
    setInfoMessage(null);
    setErrorMessage(null);

    try {
      const data: AnalysisResult = await fetchService(Config.endpoints.MULTI_PERIOD_ANALYSIS, {
        method: 'POST',
        body: JSON.stringify({
          companyCode: selectedCompanyCode!.value,
          fromPeriod: selectedFromPeriod!.value,
          toPeriod: selectedToPeriod!.value,
        }),
      })
        .then(handleErrors({ errorMessage: 'Multi-period analysis failed.', parseResponseErrorMessage: true }))
        .then(parseAsJson);
      if (data.status === 'no_data') {
        setInfoMessage(data.message);
      } else {
        setResult(data);
      }
    } catch (err: any) {
      const message = err?.message || 'An unexpected error occurred.';
      setErrorMessage(message);
      toastr.error('', 'Multi-period analysis failed.');
    } finally {
      setIsLoading(false);
    }
  }, [canSubmit, selectedCompanyCode, selectedFromPeriod, selectedToPeriod]);

  const handleCopyToClipboard = useCallback(() => {
    if (result?.analysis) {
      navigator.clipboard.writeText(result.analysis).then(
        () => toastr.success('', 'Analysis copied to clipboard.'),
        () => toastr.error('', 'Unable to copy to clipboard.')
      );
    }
  }, [result?.analysis]);

  if (!isPwcUser) {
    return (
      <div className="page">
        <div className="container-fluid">
          <div className="row no-gutters page-content-container">
            <div className="col-12">
              <div className="alert alert-warning mt-3">
                This feature is only available to PwC users.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatElapsed = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const hasNoResults = !isLoading && !result && !errorMessage && !infoMessage;

  return (
    <div className="page multi-period-analysis">
      <div className="container-fluid">
        <div className="row no-gutters page-content-container">
          <div className="col-12">
            <h2 className="page-title">Multi-Period Analysis</h2>

            <div className="analysis-filters">
              <div className="analysis-filter-item analysis-filter-item--company">
                <label className="form-label">Company Code</label>
                <Select
                  className="select-company-code"
                  name="companyCode"
                  value={selectedCompanyCode}
                  options={companyCodes}
                  onChange={setSelectedCompanyCode}
                  placeholder="Select company code..."
                  disabled={isLoading}
                />
              </div>
              <div className="analysis-filter-item analysis-filter-item--period">
                <label className="form-label">From Period</label>
                <Select
                  className="select-from-period"
                  name="fromPeriod"
                  value={selectedFromPeriod}
                  options={periodOptions}
                  onChange={handleFromPeriodChange}
                  placeholder="Select from period..."
                  disabled={isLoading}
                />
              </div>
              <div className="analysis-filter-item analysis-filter-item--period">
                <label className="form-label">To Period</label>
                <Select
                  className="select-to-period"
                  name="toPeriod"
                  value={selectedToPeriod}
                  options={toPeriodOptions}
                  onChange={setSelectedToPeriod}
                  placeholder="Select to period..."
                  disabled={isLoading || !selectedFromPeriod}
                />
              </div>
            </div>

            <div className="mb-3">
              <Button
                onClick={handleSubmit}
                kind="secondary"
                disabled={!canSubmit}
                isLoading={isLoading}
              >
                {isLoading ? 'Analyzing...' : 'Run Analysis'}
              </Button>
            </div>

            {isLoading && (
              <div className="text-center my-4">
                <Spinner />
                <p className="mt-2 text-muted">
                  Generating multi-period analysis&hellip;
                </p>
                <p className="analysis-elapsed-timer text-muted">
                  Elapsed: {formatElapsed(elapsedSeconds)}
                </p>
              </div>
            )}

            {errorMessage && !isLoading && (
              <div className="alert alert-danger" role="alert">
                {errorMessage}
              </div>
            )}

            {infoMessage && !isLoading && (
              <div className="alert alert-warning" role="alert">
                {infoMessage}
              </div>
            )}

            {hasNoResults && (
              <div className="analysis-empty-state">
                <p><strong>Compare tax provision data across two periods</strong></p>
                <p>
                  Select a Company Code, a From Period and a To Period, then click
                  &ldquo;Run Analysis&rdquo; to generate an AI&#8209;powered comparative summary
                  of Tax Expense, GAAP Trial Balance, Adjustments and Book to Tax Differences.
                </p>
              </div>
            )}

            {result && !isLoading && (
              <div className="card analysis-result-card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span>
                    Analysis: {result.metadata.companyCode}
                    {' '}({result.metadata.fromPeriod} &rarr; {result.metadata.toPeriod})
                  </span>
                  <Button
                    onClick={handleCopyToClipboard}
                    kind="transparent"
                  >
                    <ClipboardIcon style={{ marginRight: '0.35rem' }} />
                    Copy to Clipboard
                  </Button>
                </div>
                <div className="card-body">
                  <div className="analysis-content">
                    {remark().use(remarkToReact, React).processSync(result.analysis).result}
                  </div>
                  {result.metadata.finishReason !== 'stop' && (
                    <div className="alert alert-warning mt-3">
                      The analysis may be incomplete (finish reason: {result.metadata.finishReason}).
                    </div>
                  )}
                </div>
                <div className="card-footer text-muted small">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      Generated at: {new Date(result.metadata.generatedAt).toLocaleString()}
                      {result.usage && (
                        <span className="ms-3">
                          Tokens used: {result.usage.total_tokens?.toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div className="analysis-disclaimer">
                      Note: Please be sure all tasks that involve AI applications
                      are conducted in accordance with{' '}
                      <a
                        href={[
                          'https://hq.pwc.com',
                          '/our-clients-and-business',
                          '/delivering-one-firm-to-clients',
                          '/artificial-intelligence-at-pwc',
                          '/business-rules-for-use-of-generative-ai.html',
                        ].join('')}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        AI Business Rules
                      </a>.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiPeriodAnalysisPage;
